import { logger } from '../../utils/logger.js'

const log = logger.create('geocode')

const PHOTON_REVERSE_URL = 'https://photon.komoot.io/reverse'

export interface ReverseGeocodeResult {
  name?: string | undefined
  street?: string | undefined
  housenumber?: string | undefined
  postcode?: string | undefined
  city?: string | undefined
  state?: string | undefined
  country?: string | undefined
  county?: string | undefined
  district?: string | undefined
  locality?: string | undefined
  osmKey?: string | undefined
  osmValue?: string | undefined
  featureType?: string | undefined
  displayName: string
}

function quantize(n: number): number {
  return Math.round(n * 100) / 100
}

const OSM_TYPE_LABELS: Partial<Record<string, Record<string, string>>> = {
  natural: {
    peak: 'Peak',
    saddle: 'Saddle',
    ridge: 'Ridge',
    cliff: 'Cliff',
    valley: 'Valley',
    water: 'Water',
    spring: 'Spring',
    glacier: 'Glacier',
    cave_entrance: 'Cave',
    hot_spring: 'Hot Spring',
    wetland: 'Wetland',
    wood: 'Forest',
    scrub: 'Scrub',
    grassland: 'Grassland',
    heath: 'Heath',
    fell: 'Fell',
    bare_rock: 'Bare Rock',
    scree: 'Scree',
  },
  highway: {
    path: 'Trail',
    track: 'Track',
    footway: 'Footway',
    bridleway: 'Bridleway',
    cycleway: 'Cycleway',
    service: 'Service Road',
    tertiary: 'Road',
    secondary: 'Road',
    primary: 'Road',
    trunk: 'Highway',
    motorway: 'Highway',
  },
  waterway: {
    river: 'River',
    stream: 'Stream',
    canal: 'Canal',
    ditch: 'Ditch',
    waterfall: 'Waterfall',
  },
  amenity: {
    shelter: 'Shelter',
    drinking_water: 'Water Source',
    parking: 'Parking',
    toilets: 'Restroom',
    campsite: 'Campsite',
  },
  tourism: {
    camp_site: 'Campsite',
    camp_pitch: 'Camp Pitch',
    viewpoint: 'Viewpoint',
    information: 'Information',
    alpine_hut: 'Alpine Hut',
    wilderness_hut: 'Wilderness Hut',
    picnic_site: 'Picnic Site',
  },
  leisure: {
    park: 'Park',
    nature_reserve: 'Nature Reserve',
  },
  place: {
    hamlet: 'Hamlet',
    village: 'Village',
    town: 'Town',
    city: 'City',
    suburb: 'Suburb',
    neighbourhood: 'Neighborhood',
    isolated_dwelling: 'Dwelling',
    locality: 'Locality',
  },
  boundary: {
    national_park: 'National Park',
    protected_area: 'Protected Area',
  },
}

function resolveFeatureType(osmKey?: string, osmValue?: string): string | undefined {
  if (!osmKey || !osmValue) return undefined
  return OSM_TYPE_LABELS[osmKey]?.[osmValue]
}

function buildDisplayName(r: Omit<ReverseGeocodeResult, 'displayName'>): string {
  const parts: string[] = []
  if (r.name) parts.push(r.name)
  else if (r.street) {
    const addr = r.housenumber ? `${r.housenumber} ${r.street}` : r.street
    parts.push(addr)
  }
  if (r.city && r.city !== r.name) parts.push(r.city)
  if (r.state) parts.push(r.state)
  return parts.length > 0 ? parts.join(', ') : (r.country ?? 'Unknown location')
}

class ReverseGeocodeService {
  private cache = new Map<string, ReverseGeocodeResult>()

  async lookup(lat: number, lon: number): Promise<ReverseGeocodeResult | null> {
    const qLat = quantize(lat)
    const qLon = quantize(lon)
    const key = `${qLat}:${qLon}`

    const cached = this.cache.get(key)
    if (cached) return cached

    try {
      const url = `${PHOTON_REVERSE_URL}?lat=${lat}&lon=${lon}`
      const response = await fetch(url)
      if (!response.ok) return null

      const data = await response.json()
      const features = data?.features
      if (!features || features.length === 0) return null

      const props = features[0].properties ?? {}
      const osmKey: string | undefined = props.osm_key ?? undefined
      const osmValue: string | undefined = props.osm_value ?? undefined
      const result: ReverseGeocodeResult = {
        name: props.name ?? undefined,
        street: props.street ?? undefined,
        housenumber: props.housenumber ?? undefined,
        postcode: props.postcode ?? undefined,
        city: props.city ?? props.town ?? props.village ?? undefined,
        state: props.state ?? undefined,
        country: props.country ?? undefined,
        county: props.county ?? undefined,
        district: props.district ?? undefined,
        locality: props.locality ?? undefined,
        osmKey,
        osmValue,
        featureType: resolveFeatureType(osmKey, osmValue),
        displayName: '',
      }
      result.displayName = buildDisplayName(result)

      this.cache.set(key, result)

      if (this.cache.size > 200) {
        const firstKey = this.cache.keys().next().value
        if (firstKey) this.cache.delete(firstKey)
      }

      return result
    } catch (err) {
      log.warn('Reverse geocode failed', err)
      return null
    }
  }
}

export const reverseGeocodeService = new ReverseGeocodeService()
